import {
  MutationUpdatePluginArgs,
  MutationCreatePluginListingArgs,
  MutationUpdatePluginListingArgs,
  MutationDeletePluginListingArgs,
  MutationCreatePluginArgs,
  MutationDeletePluginArgs,
  MutationAddPluginCollaboratorArgs,
  MutationRemovePluginCollaboratorArgs,
} from '../generated';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import { token } from '@trello/session-cookie';
import { trelloFetch, fetch } from '@trello/fetch';
import type { TrelloRestResolver } from '../types';
import { getNetworkClient } from '../getNetworkClient';
import { isQueryInfo } from '../isQueryInfo';

export const updatePlugin: TrelloRestResolver<MutationUpdatePluginArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  if (!args.fields) {
    throw new Error('Expected fields argument is missing.');
  }

  const networkClient = getNetworkClient();

  const response = await fetch(
    networkClient.getUrl(`/1/plugin/${args.pluginId}`),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        ...args.fields,
        token: context.token || token,
      }),
    },
  );

  const plugin = await response.json();

  return prepareDataForApolloCache(plugin, rootNode);
};

export const deletePluginListing: TrelloRestResolver<MutationDeletePluginListingArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/plugin/${args.pluginId}/listings/${args.pluginListingId}`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        ...args,
        token: context.token || token,
      }),
    },
  );

  const listing = await response.json();

  return prepareDataForApolloCache(listing, rootNode);
};

export const updatePluginListing: TrelloRestResolver<MutationUpdatePluginListingArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/plugin/${args.pluginId}/listings/${args.pluginListingId}`,
    ),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        ...args,
        token: context.token || token,
      }),
    },
  );

  const listing = await response.json();

  return prepareDataForApolloCache(listing, rootNode);
};

export const createPluginListing: TrelloRestResolver<MutationCreatePluginListingArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/plugin/${args.pluginId}/listings`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        ...args,
        token: context.token || token,
      }),
    },
  );

  const listing = await response.json();

  return prepareDataForApolloCache(listing, rootNode);
};

export const createPlugin: TrelloRestResolver<MutationCreatePluginArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(networkClient.getUrl(`/1/plugin`), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      idAgreement: args.agreementId,
      idOrganizationOwner: args.organizationId,
      iframeConnectorUrl: args.iframeConnectorUrl,
      listings: args.listings,
      author: args.author || '',
      token: context.token || token,
    }),
  });

  const plugin = await response.json();

  return prepareDataForApolloCache(plugin, rootNode);
};

export const deletePlugin: TrelloRestResolver<MutationDeletePluginArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/plugins/${args.pluginId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  await response.json();

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const pluginCollaboratorsResolver: TrelloRestResolver<object> = async (
  plugin: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/plugin/${plugin.id}/collaborators`);

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Plugin.collaborators',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([401, 404].includes(response.status)) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode, 'Plugin') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

const updatePluginCollaborator: TrelloRestResolver<{
  pluginId: string;
  memberId: string;
  method: string;
}> = async (
  plugin: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/plugin/${args.pluginId}/collaborators`,
  );
  const method = args.method;

  try {
    const response = await fetch(apiUrl, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        idCollaborator: args.memberId,
        token: context.token || token,
      }),
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if ([401, 404].includes(response.status)) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode) : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const addPluginCollaborator: TrelloRestResolver<MutationAddPluginCollaboratorArgs> = async (
  plugin: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const mutationArgs = {
    ...args,
    method: 'PUT',
  };
  return updatePluginCollaborator(plugin, mutationArgs, context, info);
};

export const removePluginCollaborator: TrelloRestResolver<MutationRemovePluginCollaboratorArgs> = async (
  plugin: {
    id: string;
  },
  args,
  context,
  info,
) => {
  const mutationArgs = {
    ...args,
    method: 'DELETE',
  };
  return updatePluginCollaborator(plugin, mutationArgs, context, info);
};
